import {
  ArrowClockwise,
  Info,
  MagnifyingGlass,
  Plus,
  SlidersHorizontal,
  WarningCircle,
} from '@phosphor-icons/react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { FILTERABLE_FIELDS } from '../../config/filterable-fields';
import {
  useCustomFieldDefinitions,
  useDeleteCustomFieldDefinition,
} from '../../hooks/use-custom-field-definitions';
import { isAttributeInUseError } from '../../utils/attribute-errors';
import {
  buildCustomFieldsTableRows,
  formatAttributeUsage,
  type CustomFieldTableRow,
} from '../../utils/custom-fields-table-rows';
import { CustomFieldFormModal } from '../custom-field-form-modal/custom-field-form-modal';
import {
  CustomFieldActionsCell,
  CustomFieldChoicesCell,
  CustomFieldLabelCell,
  CustomFieldFilterableCell,
} from '../custom-fields-table/custom-fields-table-columns';

type ContactsManageFieldsProps = {
  embedded?: boolean;
  variant?: 'embedded' | 'page';
};

const TypePill = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-[12px] font-medium leading-4 text-neutral-600">
    <span className="size-1.5 rounded-full bg-neutral-400" aria-hidden />
    {label}
  </span>
);

export const ContactsManageFields = ({
  embedded = false,
  variant = embedded ? 'embedded' : 'page',
}: ContactsManageFieldsProps) => {
  const isPage = variant === 'page';
  const { toast } = useToast();
  const { data, isLoading, error, refetch } = useCustomFieldDefinitions();
  const { mutateAsync: deleteField } = useDeleteCustomFieldDefinition();

  const [query, setQuery] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<CustomFieldTableRow | undefined>(undefined);
  const [deletingRow, setDeletingRow] = useState<CustomFieldTableRow | undefined>(undefined);
  const [blockedRow, setBlockedRow] = useState<CustomFieldTableRow | undefined>(undefined);

  const allRows = useMemo(() => {
    const rows = buildCustomFieldsTableRows(FILTERABLE_FIELDS, data?.items ?? []);
    return isPage ? rows.filter((row) => row.tier !== 'native') : rows;
  }, [data?.items, isPage]);

  const rows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return allRows;

    return allRows.filter((row) => {
      const haystack = [row.label, row.key, row.dataTypeLabel, row.tier, ...row.choices]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [allRows, query]);

  const isError = Boolean(error);
  const errorMessage = error instanceof Error ? error.message : 'Failed to load fields';
  const showLoading = isLoading && allRows.length === 0;

  const openEdit = (row: CustomFieldTableRow) => {
    if (row.tier === 'native') return;
    setEditingRow(row);
    setFormModalOpen(true);
  };

  const handleCreateClick = () => {
    setEditingRow(undefined);
    setFormModalOpen(true);
  };

  const handleDeleteClick = (row: CustomFieldTableRow) => {
    if (row.usageCount > 0) {
      setBlockedRow(row);
      return;
    }
    setDeletingRow(row);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRow?.key) return;
    try {
      await deleteField(deletingRow.key);
      toast({
        variant: 'success',
        title: 'Custom field deleted.',
      });
      setDeletingRow(undefined);
    } catch (err) {
      if (isAttributeInUseError(err)) {
        setBlockedRow(deletingRow);
        setDeletingRow(undefined);
        return;
      }
      toast({
        variant: 'destructive',
        title: 'Failed to delete field',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const tableHeadClassName = cn(
    'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600'
  );

  const renderEmbeddedTable = () => (
    <Table divClassname="min-w-0 border rounded-md overflow-x-hidden" className="table-fixed">
      <TableHeader className="bg-muted/40">
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-[28%] px-4 text-foreground/80">Label</TableHead>
          <TableHead className="w-[14%] px-4 text-foreground/80">Data type</TableHead>
          <TableHead className="w-24 px-4 text-foreground/80">Filterable</TableHead>
          <TableHead className="px-4 text-foreground/80">Choices</TableHead>
          <TableHead className="w-14 px-4 pr-5">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {renderTableBody(
          (row) => (
            <>
              <TableCell className="px-4 py-2.5">
                <CustomFieldLabelCell row={row} />
              </TableCell>
              <TableCell className="px-4 py-2.5 text-foreground/80">{row.dataTypeLabel}</TableCell>
              <TableCell className="px-4 py-2.5">
                <CustomFieldFilterableCell isFilterable={row.isFilterable} />
              </TableCell>
              <TableCell className="px-4 py-2.5">
                <CustomFieldChoicesCell choices={row.choices} dataTypeLabel={row.dataTypeLabel} />
              </TableCell>
              <TableCell
                className="px-4 py-2.5 pr-5 text-right"
                onClick={(event) => event.stopPropagation()}
              >
                <CustomFieldActionsCell row={row} onEdit={openEdit} onDelete={handleDeleteClick} />
              </TableCell>
            </>
          ),
          5
        )}
      </TableBody>
    </Table>
  );

  const renderPageTable = () => (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[880px] border-collapse text-sm leading-5">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              <th className={tableHeadClassName}>Label</th>
              <th className={tableHeadClassName}>Key</th>
              <th className={tableHeadClassName}>Type</th>
              <th className={tableHeadClassName}>Options</th>
              <th className={tableHeadClassName}>Required</th>
              <th className={tableHeadClassName}>In use</th>
              <th className={cn(tableHeadClassName, 'w-12')} />
            </tr>
          </thead>
          <tbody>
            {renderTableBody(
              (row) => (
                <>
                  <td className="px-4 py-3 align-middle text-sm font-medium leading-5 text-neutral-900">
                    {row.label}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <code className="font-mono text-[13px] leading-[18px] text-neutral-600">
                      {row.key}
                    </code>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <TypePill label={row.dataTypeLabel} />
                  </td>
                  <td className="max-w-[260px] px-4 py-3 align-middle text-sm leading-5 text-neutral-600">
                    <div
                      className="truncate"
                      title={row.choices.length > 0 ? row.choices.join(', ') : undefined}
                    >
                      {row.choices.length > 0 ? row.choices.join(', ') : '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-sm leading-5 text-neutral-600">
                    {row.required ? 'Yes' : '—'}
                  </td>
                  <td className="px-4 py-3 align-middle text-sm leading-5 text-neutral-600">
                    {formatAttributeUsage(row.usageCount)}
                  </td>
                  <td
                    className="px-4 py-3 align-middle text-center"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <CustomFieldActionsCell
                      row={row}
                      onEdit={openEdit}
                      onDelete={handleDeleteClick}
                    />
                  </td>
                </>
              ),
              7,
              'data-row border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50'
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  function renderTableBody(
    renderCells: (row: CustomFieldTableRow) => ReactNode,
    colSpan: number,
    rowClassName = 'hover:bg-muted/40'
  ) {
    if (showLoading) {
      if (isPage) {
        return Array.from({ length: 4 }).map((_, index) => (
          <tr key={`skeleton-${index}`} className="border-b border-neutral-100">
            <td className="px-4 py-3">
              <span className="block h-3.5 w-[120px] animate-pulse rounded bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3">
              <span className="block h-3.5 w-20 animate-pulse rounded bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3">
              <span className="block h-5 w-16 animate-pulse rounded-full bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3">
              <span className="block h-3.5 w-[150px] animate-pulse rounded bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3">
              <span className="block h-3.5 w-8 animate-pulse rounded bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3">
              <span className="block h-3.5 w-[90px] animate-pulse rounded bg-[#EDEEF0]" />
            </td>
            <td className="px-4 py-3" />
          </tr>
        ));
      }

      return Array.from({ length: 4 }).map((_, index) => (
        <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
          <TableCell className="px-4 py-2.5">
            <span className="block h-3.5 w-[130px] animate-pulse rounded bg-[#EDEEF0]" />
          </TableCell>
          <TableCell className="px-4 py-2.5">
            <span className="block h-3.5 w-16 animate-pulse rounded bg-[#EDEEF0]" />
          </TableCell>
          <TableCell className="px-4 py-2.5">
            <span className="block size-4 animate-pulse rounded bg-[#EDEEF0]" />
          </TableCell>
          <TableCell className="px-4 py-2.5">
            <span className="block h-5 w-24 animate-pulse rounded-full bg-[#EDEEF0]" />
          </TableCell>
          <TableCell className="px-4 py-2.5 pr-5" />
        </TableRow>
      ));
    }

    if (!showLoading && isError) {
      if (isPage) {
        return (
          <tr>
            <td colSpan={colSpan} className="px-4 py-14">
              <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2.5 text-center">
                <WarningCircle className="size-7 text-error" aria-hidden />
                <div className="text-base font-semibold leading-6 text-neutral-900">
                  Could not load custom fields
                </div>
                <p className="text-sm leading-5 text-neutral-600">
                  Something went wrong while fetching your attribute definitions. Check your
                  connection and try again.
                </p>
                <Button
                  type="button"
                  className="mt-1 bg-primary-600 hover:bg-primary-700"
                  onClick={() => void refetch()}
                >
                  <ArrowClockwise className="size-4" aria-hidden />
                  Retry
                </Button>
              </div>
            </td>
          </tr>
        );
      }

      return (
        <TableRow>
          <TableCell colSpan={colSpan} className="py-10 text-center text-sm text-muted-foreground">
            {errorMessage}
          </TableCell>
        </TableRow>
      );
    }

    if (!showLoading && !isError && rows.length === 0) {
      if (isPage && !query.trim()) {
        return (
          <tr>
            <td colSpan={colSpan} className="px-4 py-14">
              <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2.5 text-center">
                <SlidersHorizontal className="size-7 text-neutral-400" aria-hidden />
                <div className="text-base font-semibold leading-6 text-neutral-900">
                  No custom fields yet
                </div>
                <p className="text-sm leading-5 text-neutral-600">
                  Create your first custom attribute to start capturing org-specific data on every
                  contact.
                </p>
                <Button
                  type="button"
                  className="mt-1 bg-primary-600 hover:bg-primary-700"
                  onClick={handleCreateClick}
                >
                  <Plus className="mr-2 size-4" aria-hidden />
                  New attribute
                </Button>
              </div>
            </td>
          </tr>
        );
      }

      const emptyMessage = query.trim() ? 'No fields match your search' : 'No fields found';
      return isPage ? (
        <tr>
          <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-neutral-500">
            {emptyMessage}
          </td>
        </tr>
      ) : (
        <TableRow>
          <TableCell colSpan={colSpan} className="py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => {
      const isEditable = row.tier !== 'native';
      const rowKey = row.itemId ?? row.key;
      const interactionProps = {
        className: cn(rowClassName, isEditable && 'cursor-pointer'),
        onClick: isEditable ? () => openEdit(row) : undefined,
      };

      if (isPage) {
        return (
          <tr key={rowKey} {...interactionProps}>
            {renderCells(row)}
          </tr>
        );
      }

      return (
        <TableRow key={rowKey} {...interactionProps}>
          {renderCells(row)}
        </TableRow>
      );
    });
  }

  const pageContent = isPage ? (
    <>
      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-[28px] font-semibold leading-9 text-neutral-900">Custom fields</h1>
          <p className="mt-0.5 text-sm leading-5 text-neutral-600">
            Attribute definitions used on every contact profile — create, edit, or retire them here.
          </p>
        </div>
        <div className="flex-1" />
        <Button
          type="button"
          className="h-10 bg-primary-600 px-4 text-white shadow-e1 hover:bg-primary-700"
          onClick={handleCreateClick}
        >
          <Plus className="mr-2 size-4" aria-hidden />
          New attribute
        </Button>
      </div>

      <div className="flex items-start gap-2.5 rounded-lg bg-primary-50 px-3.5 py-2.5">
        <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
        <p className="text-[13px] leading-[18px] text-primary-900">
          Fields created or edited here appear immediately in the Add/Edit contact drawer and on the
          contact detail page — try it from{' '}
          <Link to="/contacts" className="font-medium underline">
            Contacts
          </Link>{' '}
          after saving one below. Values are validated against these definitions when a contact is
          created or edited (#48/#53).
        </p>
      </div>

      {renderPageTable()}
    </>
  ) : (
    <>
      <div>
        {!embedded && (
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">Manage Fields</h1>
        )}
        <p className={`max-w-3xl text-sm text-muted-foreground ${embedded ? '' : 'mt-1'}`}>
          Native fields are read-only. Default fields can be edited but not deleted. Custom fields
          you create can be fully edited or deleted.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-[min(100%,20rem)]">
          <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search fields"
            className="h-10 rounded-md border-input pl-10 text-sm"
            aria-label="Search fields by name or type"
          />
        </div>
        <Button
          type="button"
          className="bg-primary-600 text-white hover:bg-primary-700"
          onClick={handleCreateClick}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create new field
        </Button>
      </div>

      {renderEmbeddedTable()}
    </>
  );

  return (
    <>
      <div className={cn('flex min-h-0 flex-1 flex-col gap-4', isPage && 'gap-5')}>
        {pageContent}
      </div>

      <CustomFieldFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        editingField={editingRow?.definition}
      />

      <ConfirmationModal
        open={Boolean(deletingRow)}
        onOpenChange={(open) => !open && setDeletingRow(undefined)}
        title={`Delete ${deletingRow?.label ?? 'field'}?`}
        description="This removes the field definition. It is not currently in use by any contact, so nothing else is affected. This action cannot be undone."
        confirmText="Delete field"
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        preventAutoClose
        onConfirm={() => void handleConfirmDelete()}
      />

      <ConfirmationModal
        open={Boolean(blockedRow)}
        onOpenChange={(open) => !open && setBlockedRow(undefined)}
        title={`Cannot delete ${blockedRow?.label ?? 'field'}`}
        description={`${blockedRow?.usageCount ?? 0} ${
          (blockedRow?.usageCount ?? 0) === 1 ? 'contact has' : 'contacts have'
        } a value for this field, so it cannot be deleted. Remove or clear those values first, or leave the field unused, before deleting it.`}
        confirmText="Got it"
        hideCancel
        onConfirm={() => setBlockedRow(undefined)}
      />
    </>
  );
};
